/**
 * âš ï¸ ConfirmDialog â€” Enterprise Confirmation Modal
 *
 * Modal de confirmação para ações críticas de governança.
 * Substitui window.confirm por UI enterprise com:
 *  - Overlay contextual (backdrop-blur)
 *  - Ícone + título + descrição
 *  - Botões Cancelar / Confirmar (com variante danger)
 *  - Loader real durante processamento
 *  - Esc para fechar
 *
 * Memoizado para performance.
 */

import React, { useEffect, useCallback } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';

export interface ConfirmDialogProps {
  /** Controla visibilidade */
  open: boolean;
  /** Título do diálogo */
  title: string;
  /** Descrição / mensagem de confirmação */
  description: string;
  /** Texto do botão de confirmação */
  confirmLabel?: string;
  /** Texto do botão de cancelamento */
  cancelLabel?: string;
  /** Se true, botão de confirmar usa estilo vermelho (danger) */
  danger?: boolean;
  /** Se true, exibe loader no botão de confirmar e desabilita interação */
  loading?: boolean;
  /** Callback de confirmação */
  onConfirm: () => void;
  /** Callback de cancelamento / fechar */
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = React.memo(
  ({
    open,
    title,
    description,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    danger = false,
    loading = false,
    onConfirm,
    onCancel,
  }) => {
    // Fechar com Esc
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !loading) onCancel();
      },
      [onCancel, loading],
    );

    useEffect(() => {
      if (open) {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
      }
    }, [open, handleKeyDown]);

    if (!open) return null;

    const confirmBtnClass = danger
      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500/30 text-white'
      : 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500/30 text-white';

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={loading ? undefined : onCancel}
        />

        {/* Dialog */}
        <div className="relative w-full max-w-md mx-4 bg-white dark:bg-[var(--bg-surface)] rounded shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-start gap-3 px-6 pt-6 pb-2">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                danger
                  ? 'bg-red-100 dark:bg-red-900/30'
                  : 'bg-amber-100 dark:bg-amber-900/30'
              }`}
            >
              <AlertTriangle
                size={20}
                className={
                  danger
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-amber-600 dark:text-amber-400'
                }
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100">
                {title}
              </h3>
              <p className="mt-1 text-xs text-gray-600 dark:text-slate-400 leading-relaxed">
                {description}
              </p>
            </div>
            {!loading && (
              <button
                onClick={onCancel}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors shrink-0"
              >
                <X size={14} className="text-gray-400" />
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 px-6 py-4">
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-xs font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={`px-4 py-2 text-xs font-semibold rounded transition-all focus:outline-none focus:ring-2 disabled:opacity-70 flex items-center gap-2 ${confirmBtnClass}`}
            >
              {loading && <Loader2 size={12} className="animate-spin" />}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

ConfirmDialog.displayName = 'ConfirmDialog';
